import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, LifeBuoy, MessageSquare, Plus, RefreshCw, Send, Ticket } from "lucide-react";
import { PageShell } from "@/pages/dashboard/RolePages";
import api, { formatApiError } from "@/lib/api";
import { useRole } from "@/context/RoleContext";

const labels = {
  open: "Abierto", in_progress: "En atención", waiting_user: "Esperando usuario",
  escalated: "Escalado", resolved: "Resuelto", closed: "Cerrado",
  technical: "Técnico", access: "Acceso", payment: "Pago", profile: "Perfil",
  academy: "Academia", marketplace: "Marketplace", diagnostics: "Diagnósticos", other: "Otro",
  low: "Baja", medium: "Media", high: "Alta", critical: "Crítica",
};

const SupportCenter = ({ staff = null, presetStatus = "", presetCategory = "", problems = false }) => {
  const { role } = useRole();
  const effectiveStaff = staff ?? ["admin", "superadmin", "soporte"].includes(role);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ subject: "", description: "", category: "technical", priority: "medium" });
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [filters, setFilters] = useState({ status: presetStatus, category: presetCategory, priority: "", assigned_to_me: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const url = effectiveStaff ? "/api/admin/support/tickets" : "/api/support/tickets/mine";
      const params = effectiveStaff ? Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== "" && value !== false)) : undefined;
      const { data } = await api.get(url, { params });
      setRows(data);
      setSelected((current) => data.find((row) => row.id === current?.id) || null);
    } catch (e) { setError(formatApiError(e)); }
  }, [effectiveStaff, filters]);

  useEffect(() => { load(); }, [load]);

  const create = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      await api.post("/api/support/tickets", form);
      setForm({ subject: "", description: "", category: "technical", priority: "medium" });
      await load();
    } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/api/support/tickets/${selected.id}/reply`, { message: reply, internal: effectiveStaff && internal });
      setReply(""); setSelected(data); await load();
    } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };

  const action = async (name) => {
    if (!selected) return;
    setBusy(true); setError("");
    const note = name === "resolve" ? window.prompt("Resolución aplicada:") : name === "escalate" ? window.prompt("Motivo del escalamiento:") : "";
    if ((name === "resolve" || name === "escalate") && note === null) { setBusy(false); return; }
    try {
      const { data } = await api.post(`/api/admin/support/tickets/${selected.id}/action`, { action: name, note: note || undefined });
      setSelected(data); await load();
    } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };

  const visibleRows = useMemo(() => {
    if (!problems) return rows;
    const latest = new Map();
    rows.filter((row) => !["resolved", "closed"].includes(row.status)).forEach((row) => {
      if (!latest.has(row.user_id)) latest.set(row.user_id, row);
    });
    return [...latest.values()];
  }, [rows, problems]);

  return (
    <PageShell
      kicker={effectiveStaff ? "Operación · Soporte KD9" : "Soporte KD9 · Seguimiento real"}
      title={problems ? "USUARIOS CON CASOS ACTIVOS" : effectiveStaff ? "CENTRO DE TICKETS" : "MIS SOLICITUDES DE SOPORTE"}
      desc="Cada caso conserva responsable, conversación, estado, prioridad y trazabilidad. Soporte no aprueba pagos ni activa Premium."
      badge={<button onClick={load} className="kd9-btn-outline px-3 py-2 rounded-lg"><RefreshCw size={14} /></button>}
      testId="support-center"
    >
      {error && <div className="mb-4 p-3 rounded-lg border border-red-800 text-red-300">{error}</div>}
      {!effectiveStaff && (
        <form onSubmit={create} className="kd9-card p-5 mb-6 grid md:grid-cols-2 gap-3">
          <input className="kd9-input p-3" placeholder="Asunto" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required minLength={4} />
          <div className="grid grid-cols-2 gap-3">
            <select className="kd9-input p-3" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {["technical","access","payment","profile","academy","marketplace","diagnostics","other"].map((x) => <option key={x} value={x}>{labels[x]}</option>)}
            </select>
            <select className="kd9-input p-3" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {["low","medium","high","critical"].map((x) => <option key={x} value={x}>{labels[x]}</option>)}
            </select>
          </div>
          <textarea className="kd9-input p-3 md:col-span-2 min-h-28" placeholder="Describe lo ocurrido, qué esperabas y qué apareció en pantalla." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required minLength={10} />
          <button disabled={busy} className="kd9-btn-primary py-3 rounded-lg md:col-span-2 flex justify-center items-center gap-2"><Plus size={15}/> Abrir ticket</button>
        </form>
      )}
      {effectiveStaff && !problems && (
        <div className="kd9-card p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <select className="kd9-input p-3" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">Todos los estados</option>{["open","in_progress","waiting_user","escalated","resolved","closed"].map((x)=><option key={x} value={x}>{labels[x]}</option>)}
          </select>
          <select className="kd9-input p-3" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
            <option value="">Todas las categorías</option>{["technical","access","payment","profile","academy","marketplace","diagnostics","other"].map((x)=><option key={x} value={x}>{labels[x]}</option>)}
          </select>
          <select className="kd9-input p-3" value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
            <option value="">Todas las prioridades</option>{["low","medium","high","critical"].map((x)=><option key={x} value={x}>{labels[x]}</option>)}
          </select>
          <label className="kd9-input p-3 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={filters.assigned_to_me} onChange={(e) => setFilters({ ...filters, assigned_to_me: e.target.checked })} />
            Solo mis casos
          </label>
        </div>
      )}
      <div className="grid lg:grid-cols-[380px_1fr] gap-5">
        <section className="kd9-card overflow-hidden">
          {visibleRows.length === 0 && <div className="p-8 text-center text-sm" style={{color:"var(--kd9-text-soft)"}}>No hay casos para esta vista.</div>}
          {visibleRows.map((row) => (
            <button key={row.id} onClick={() => setSelected(row)} className="w-full text-left p-4 border-b last:border-0" style={{borderColor:"var(--kd9-border)", background:selected?.id===row.id?"rgba(212,175,55,.08)":"transparent"}}>
              <div className="flex justify-between gap-2"><strong className="font-mont text-sm">{row.subject}</strong><span className="text-[10px] uppercase">{labels[row.priority]}</span></div>
              <div className="text-[11px] mt-1" style={{color:"var(--kd9-text-soft)"}}>{row.code} · {labels[row.status]} · {labels[row.category]}</div>
              {effectiveStaff && <div className="text-[11px] mt-1">{row.user_name || row.user_email}</div>}
            </button>
          ))}
        </section>
        <section className="kd9-card p-5 min-h-80">
          {!selected ? <div className="h-full flex items-center justify-center text-sm" style={{color:"var(--kd9-text-soft)"}}><Ticket className="mr-2"/> Selecciona un ticket</div> : <>
            <div className="flex flex-wrap justify-between gap-3 border-b pb-4 mb-4" style={{borderColor:"var(--kd9-border)"}}>
              <div><h3 className="font-cinzel kd9-gold-text text-xl">{selected.subject}</h3><div className="text-xs">{selected.code} · {labels[selected.status]} · {selected.assigned_name || "Sin asignar"}</div></div>
              {effectiveStaff && <div className="flex flex-wrap gap-2">
                {selected.status==="open" && <button onClick={()=>action("claim")} className="kd9-btn-outline px-3 py-2 rounded-lg">Tomar</button>}
                {["open","in_progress"].includes(selected.status) && <button onClick={()=>action("wait_user")} className="kd9-btn-outline px-3 py-2 rounded-lg">Esperar usuario</button>}
                {!["resolved","closed","escalated"].includes(selected.status) && <button onClick={()=>action("escalate")} className="kd9-btn-outline px-3 py-2 rounded-lg">Escalar</button>}
                {!["resolved","closed"].includes(selected.status) && <button onClick={()=>action("resolve")} className="kd9-btn-primary px-3 py-2 rounded-lg">Resolver</button>}
                {selected.status==="resolved" && <button onClick={()=>action("close")} className="kd9-btn-primary px-3 py-2 rounded-lg">Cerrar</button>}
                {["resolved","closed"].includes(selected.status) && <button onClick={()=>action("reopen")} className="kd9-btn-outline px-3 py-2 rounded-lg">Reabrir</button>}
              </div>}
            </div>
            <p className="font-cormorant text-lg mb-5 whitespace-pre-wrap">{selected.description}</p>
            <div className="space-y-3 mb-5">
              {(selected.messages || []).map((msg,index)=><div key={index} className="p-3 rounded-lg border" style={{borderColor:"var(--kd9-border)",background:msg.internal?"rgba(168,85,247,.08)":"transparent"}}>
                <div className="text-[10px] uppercase tracking-widest mb-1">{msg.author_name} · {msg.author_role}{msg.internal?" · Nota interna":""}</div>
                <p className="whitespace-pre-wrap text-sm">{msg.message}</p>
              </div>)}
            </div>
            {selected.status!=="closed" && <div>
              <textarea className="kd9-input p-3 w-full min-h-24" value={reply} onChange={(e)=>setReply(e.target.value)} placeholder="Escribe una respuesta verificable..." />
              <div className="flex justify-between items-center mt-2">
                {effectiveStaff ? <label className="text-xs flex gap-2"><input type="checkbox" checked={internal} onChange={(e)=>setInternal(e.target.checked)}/> Nota interna</label> : <span/>}
                <button disabled={busy||!reply.trim()} onClick={sendReply} className="kd9-btn-primary px-4 py-2 rounded-lg flex items-center gap-2"><Send size={13}/> Enviar</button>
              </div>
            </div>}
          </>}
        </section>
      </div>
    </PageShell>
  );
};

export default SupportCenter;
